import { Fragment } from 'react';
import { Button, Card, Divider, FlexBox, Li, Link, Typography, Ul } from '@vp/swan';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { ProductTierSection } from '../ProductTierSection';
import { getFeature, getFeatureSubset } from '../pageData/featuresConfig';
import { BASE_TIER } from '../pageData/tierConfig';
import clickEventData from '../utils/clickEventData';
import { Popover } from './Popover';
import type { Tier } from '../pageData/tierConfig';

interface TierCardProps {
    tier: Tier;
}

export function TierCard({ tier }: TierCardProps) {
    const { pageData } = usePage();
    const inclusions = getFeatureSubset(tier.features, [
        'suitableFor',
        'turnaroundTime',
        'revisions',
        'designerMatching',
        'fileFormat',
    ]);
    const { id } = tier;

    const revisionComments = getFeature(tier.features, 'revisionComments');
    const messaging = getFeature(tier.features, 'messaging');
    const backgroundColor = id === BASE_TIER ? 'standard' : 'strong';

    const briefURL = tier.briefURL;
    const pageZone = 'Tier Tiles';

    return (
        <Card bordered p={5} bgc={backgroundColor} key={id + 'card'}>
            <ProductTierSection tier={tier} price={tier.price} />
            <Typography fontSkin="title-item">Inclusions</Typography>
            <Divider mt={4} />
            <Ul skin="minimal" mt={4} mb={6}>
                {inclusions.map((inclusion, index) => {
                    const descriptionText = inclusion.mobileDescription
                        ? inclusion.mobileDescription
                        : inclusion.description?.text || '';
                    return (
                        <Fragment key={inclusion.title + index}>
                            <Li ml={4}>
                                <FlexBox
                                    flexDirection="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography mr={5}>{descriptionText}</Typography>
                                    {inclusion.popover && <Popover content={inclusion.popover} />}
                                </FlexBox>
                            </Li>
                            <Divider mt={2} />
                        </Fragment>
                    );
                })}
            </Ul>
            <Typography fontSkin="title-item">Communications</Typography>
            <Divider mt={4} />
            <Ul skin="minimal" mt={4}>
                <Li key={revisionComments.title} ml={4}>
                    <FlexBox flexDirection="row" justifyContent="space-between" alignItems="center">
                        {revisionComments.description?.text}
                        {revisionComments.popover && <Popover content={revisionComments.popover} />}
                    </FlexBox>
                </Li>
                <Divider mt={2} />
                <Li key={messaging.title} ml={4}>
                    <FlexBox flexDirection="row" justifyContent="space-between">
                        {id === BASE_TIER ? (
                            <Typography textColor="subtle">
                                {messaging.mobileDescription}
                            </Typography>
                        ) : (
                            messaging.mobileDescription
                        )}
                        {messaging.popover && <Popover content={messaging.popover} />}
                    </FlexBox>
                </Li>
                <Divider mt={2} />
            </Ul>
            <Button
                mt={5}
                skin="primary"
                width="full-width"
                render={({ children, ...rest }) => (
                    <Link
                        to={briefURL}
                        onClick={() => {
                            sendNavigationEvent({
                                ...clickEventData({
                                    pagePath: pageData.pagePath,
                                    ctaUrl: briefURL,
                                    ctaValue: 'Get Started-CTA',
                                    pageZone,
                                }),
                                ...pageData,
                            });
                        }}
                        {...rest}
                    >
                        {children}
                    </Link>
                )}
            >
                Continue to brief
            </Button>
        </Card>
    );
}
